import type { SimpleTableConfig, BaseEntity, EntityWithRelations } from "../types";
import type { CalendarEvent } from "@west-tennessee-consulting/ui/types/calendar";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";

/**
 * Get all date-type fields from a table config
 */
export function getDateFields<T extends BaseEntity>(config: SimpleTableConfig<T>): Array<{
  id: string;
  label: string;
  type: string;
}> {
  const dateTypes = ["date", "datetime", "due-date", "date-worked"];

  return Object.entries(config.fields)
    .filter(([_, field]) => dateTypes.includes(field.type || ""))
    .map(([id, field]) => ({
      id,
      label: field.label,
      type: field.type || "date",
    }));
}

/**
 * Get all text-type fields from a table config (for color coding)
 */
export function getTextFields<T extends BaseEntity>(config: SimpleTableConfig<T>): Array<{
  id: string;
  label: string;
  type: string;
}> {
  const textTypes = ["text", "select", "status", "priority"];

  return Object.entries(config.fields)
    .filter(([_, field]) => textTypes.includes(field.type || ""))
    .map(([id, field]) => ({
      id,
      label: field.label,
      type: field.type || "text",
    }));
}

/**
 * Convert a UUID value to a friendly name
 * Handles common entity types like users, contacts, companies, etc.
 */
export async function getDisplayNameForValue(
  value: string | null | undefined,
  fieldType?: string
): Promise<string> {
  if (!value) return "Unknown";

  // If it's not a UUID format, return as-is
  if (!isUUID(value)) return String(value);

  try {
    const supabase = getBrowserSupabaseClient();

    // Try to fetch from app_users table first (most common case)
    const { data: userData, error: userError } = await supabase
      .from("app_users")
      .select("id, first_name, last_name, email")
      .eq("id", value)
      .single();

    if (!userError && userData) {
      return `${userData.first_name} ${userData.last_name}`.trim() || userData.email || value;
    }

    // Try to fetch from companies table
    const { data: companyData, error: companyError } = await supabase
      .from("companies")
      .select("id, name")
      .eq("id", value)
      .single();

    if (!companyError && companyData) {
      return companyData.name || value;
    }

    // Try to fetch from contacts table
    const { data: contactData, error: contactError } = await supabase
      .from("contacts")
      .select("id, first_name, last_name, email")
      .eq("id", value)
      .single();

    if (!contactError && contactData) {
      return (
        `${contactData.first_name} ${contactData.last_name}`.trim() || contactData.email || value
      );
    }
  } catch (err) {
    console.error("Error fetching display name:", err);
  }

  // Fallback to truncated UUID
  return value.substring(0, 8);
}

/**
 * Check if a string is a valid UUID
 */
function isUUID(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

/**
 * Generate a consistent color for a given value
 * Uses a hash-based approach to ensure same value always gets same color
 */
export function getColorForValue(value: string | null | undefined): string {
  if (!value) return "#e5e7eb"; // gray-200 for null/empty

  // Predefined color palette
  const colors = [
    "#ef4444", // red-500
    "#f97316", // orange-500
    "#eab308", // yellow-500
    "#22c55e", // green-500
    "#06b6d4", // cyan-500
    "#3b82f6", // blue-500
    "#8b5cf6", // violet-500
    "#ec4899", // pink-500
    "#14b8a6", // teal-500
    "#f59e0b", // amber-500
  ];

  // Simple hash function to get consistent color for value
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

/**
 * Get unique values and their colors for a field
 */
export function getColorLegend(
  data: BaseEntity[],
  fieldId: string
): Array<{ value: string; color: string; count: number }> {
  const valueMap = new Map<string, number>();

  data.forEach((item) => {
    const value = String((item as EntityWithRelations)[fieldId] ?? "Unknown");
    valueMap.set(value, (valueMap.get(value) || 0) + 1);
  });

  return Array.from(valueMap.entries())
    .map(([value, count]) => ({
      value,
      color: getColorForValue(value),
      count,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Transform a single entity to a CalendarEvent
 */
export function transformEntityToCalendarEvent<T extends BaseEntity>(
  entity: T,
  dateFieldId: string,
  config: SimpleTableConfig<T>,
  colorFieldId?: string
): CalendarEvent | null {
  const typed = entity as EntityWithRelations<T>;
  const dateValue = entity[dateFieldId as keyof T];

  if (!dateValue) {
    return null;
  }

  const event: CalendarEvent = {
    id: entity.id,
    title: typed.title ?? typed.name ?? `${config.entity} #${entity.id}`,
    description: typed.description ?? "",
    start: String(dateValue),
    end: String(dateValue),
    source: config.entity,
  };

  // Add color if color field is specified
  if (colorFieldId) {
    const colorValue = typed[colorFieldId as keyof typeof typed];
    event.backgroundColor = getColorForValue(colorValue as string | null | undefined);
    event.borderColor = getColorForValue(colorValue as string | null | undefined);
    event.textColor = "#ffffff";
  }

  // Add relevant custom metadata for CRM display
  const customMetadata: Record<string, string | number | null> = {};

  if (typed.status) {
    customMetadata.status = typed.status;
  }

  if (typed.priority) {
    customMetadata.priority = typed.priority;
  }

  if (typed.assigned_to) {
    customMetadata.assigned_to = typed.assigned_to;
  } else if (Array.isArray(typed.assigned_users) && typed.assigned_users.length > 0) {
    const firstUser = typed.assigned_users[0];
    if (typeof firstUser === "string") {
      customMetadata.assigned_to = firstUser;
    } else if (firstUser && typeof firstUser === "object") {
      const u = firstUser as { first_name?: string | null; last_name?: string | null; email?: string | null };
      customMetadata.assigned_to =
        u.first_name && u.last_name
          ? `${u.first_name} ${u.last_name}`
          : u.email ?? null;
    }
  }

  if (typed.company_id) {
    customMetadata.company_id = typed.company_id;
  } else if (typed.company_name) {
    customMetadata.company = typed.company_name;
  }

  if (Object.keys(customMetadata).length > 0) {
    event.customMetadata = customMetadata;
  }

  return event;
}

/**
 * Transform multiple entities to CalendarEvents
 */
export function transformEntitiesToCalendarEvents<T extends BaseEntity>(
  entities: T[],
  dateFieldId: string,
  config: SimpleTableConfig<T>,
  colorFieldId?: string
): CalendarEvent[] {
  return entities
    .map((entity) => transformEntityToCalendarEvent(entity, dateFieldId, config, colorFieldId))
    .filter((event): event is CalendarEvent => event !== null);
}
